import type { ReactNode } from 'react';
import React, { useMemo } from 'react';

interface TextHighlighterProps {
  value: string;
  match: string | RegExp;
  formatter?: (text: string) => ReactNode;
  ignoreCase?: boolean;
}

export const TextHighlighter = ({
  value,
  match,
  formatter = (text: string) => <strong>{text}</strong>,
  ignoreCase = true,
}: TextHighlighterProps) => {
  const highlightedContent = useMemo(() => {
    if (!value) {
      return null;
    }

    if (!match) {
      return value;
    }

    try {
      let pattern: RegExp;

      if (match instanceof RegExp) {
        let flags = match.flags;

        if (ignoreCase) {
          if (!flags.includes('i')) {
            flags += 'i';
          }
        } else {
          flags = flags.replace('i', '');
        }

        pattern = new RegExp(match.source, flags);
      } else {
        const flags = ignoreCase ? 'gi' : 'g';
        pattern = new RegExp(match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), flags);
      }

      const matches = Array.from(value.matchAll(pattern));

      if (matches.length === 0) {
        return value;
      }

      const result: ReactNode[] = [];
      let lastIndex = 0;

      matches.forEach((matchResult, idx) => {
        const matchIndex = matchResult.index!;
        const matchText = matchResult[0];

        if (matchIndex > lastIndex) {
          const textBefore = value.substring(lastIndex, matchIndex);
          result.push(<React.Fragment key={`before-${idx}`}>{textBefore}</React.Fragment>);
        }

        result.push(<React.Fragment key={`match-${idx}`}>{formatter(matchText)}</React.Fragment>);

        lastIndex = matchIndex + matchText.length;
      });

      if (lastIndex < value.length) {
        const textAfter = value.substring(lastIndex);
        result.push(<React.Fragment key="after">{textAfter}</React.Fragment>);
      }

      return result;
    } catch (error) {
      console.error('Error in TextHighlighter:', error);
      return value;
    }
  }, [value, match, formatter, ignoreCase]);

  return <>{highlightedContent}</>;
};

